import { useEffect, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'

import { getDynamicRule } from '../../../api/dynamicRules.js'
import { listGroupConfigs } from '../../../api/groupConfigs.js'

const RULE_TYPES = ['Value', 'MLRule', 'Cyclic', 'ETL']

export default function DynamicRuleDetailPage() {
  const { applicationId } = useOutletContext()
  const { dynamicRuleId } = useParams()

  const [state, setState] = useState({
    status: 'loading',
    rule: null,
    error: null,
  })
  const [values, setValues] = useState({
    name: '',
    groupConfigName: '',
    ruleType: 'Value',
    userParam: '',
  })

  useEffect(() => {
    let cancelled = false
    setState({
      status: 'loading',
      rule: null,
      error: null,
    })

    Promise.allSettled([getDynamicRule(applicationId, dynamicRuleId), listGroupConfigs(applicationId)])
      .then((results) => {
        if (cancelled) return
        const [ruleResult, groupConfigsResult] = results
        if (ruleResult.status !== 'fulfilled') {
          throw ruleResult.reason
        }
        const rule = ruleResult.value
        const groupConfigs = groupConfigsResult.status === 'fulfilled' ? groupConfigsResult.value : []
        const groupConfigsById = {}
        for (const gc of groupConfigs || []) {
          groupConfigsById[String(gc.id)] = gc
        }
        const groupConfigName = rule?.groupConfigId != null
          ? (groupConfigsById[String(rule.groupConfigId)]?.name || `#${rule.groupConfigId}`)
          : ''
        setState({
          status: 'ok',
          rule,
          error: null,
        })
        setValues({
          name: rule?.name || '',
          groupConfigName,
          ruleType: rule?.ruleType || 'Value',
          userParam: rule?.userParam || '',
        })
      })
      .catch((error) => {
        if (cancelled) return
        setState({
          status: 'error',
          rule: null,
          error,
        })
      })

    return () => {
      cancelled = true
    }
  }, [applicationId, dynamicRuleId])

  if (state.status === 'loading') {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">Loading dynamic rule...</div>
      </section>
    )
  }

  if (state.status === 'error' || !state.rule) {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">Dynamic rule not found</div>
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error || '')}</pre>
        <div className="sectionActions">
          <Link to={`/applications/${applicationId}/dynamic-rules`} className="button buttonGhost">
            Back to dynamic rules
          </Link>
        </div>
      </section>
    )
  }
  const rule = state.rule

  function onChange(field) {
    return (e) => setValues((prev) => ({ ...prev, [field]: e.target.value }))
  }

  return (
    <div className="page">
      <div className="pageHeader">
        <div>
          <h1 className="pageTitle">Dynamic rule: {rule.name || 'Untitled'}</h1>
        </div>
        <div className="buttonGroup">
          <Link to={`/applications/${applicationId}/dynamic-rules`} className="button buttonGhost">
            Back
          </Link>
        </div>
      </div>

      {state.error ? <div className="formError">{String(state.error?.message || state.error)}</div> : null}

      <section className="card formCard">
        <div className="cardTitle">View</div>
        <div className="formGrid">
          <label className="formField">
            <span className="formLabel">Name</span>
            <input className="textInput" value={values.name} onChange={onChange('name')} disabled />
          </label>

          <label className="formField">
            <span className="formLabel">Type</span>
            <select className="textInput" value={values.ruleType} onChange={onChange('ruleType')} disabled>
              {RULE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Group config</span>
            <input className="textInput" value={values.groupConfigName} onChange={onChange('groupConfigName')} disabled />
          </label>

          <label className="formField">
            <span className="formLabel">{values.ruleType === 'ETL' ? 'ETL table name' : 'User param'}</span>
            <input className="textInput" value={values.userParam} onChange={onChange('userParam')} disabled />
          </label>
        </div>
      </section>

      <section className="card formCard cardSectionTop">
        <div className="cardTitle">Dynamic rule elements</div>
        {!Array.isArray(rule.dynamicRuleElements) || rule.dynamicRuleElements.length === 0 ? (
          <p className="muted inlineMessage">
            No elements.
          </p>
        ) : (
          <table className="table" aria-label="Dynamic rule elements">
            <thead>
              <tr>
                <th>Left boundary</th>
                <th>Right boundary</th>
                <th>Output</th>
                <th>Default</th>
              </tr>
            </thead>
            <tbody>
              {rule.dynamicRuleElements.map((item, index) => (
                <tr key={`${rule.id}_${index}`}>
                  <td>{item?.left_boundary || ''}</td>
                  <td>{item?.right_boundary || ''}</td>
                  <td>
                    <span className="mono">{item?.output || ''}</span>
                  </td>
                  <td>{item?.is_default ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
